import { useState, useEffect, useRef } from "react";
import {
  useElements,
  useStripe,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
// Config
import { isDevelopment } from "../../../config/env";
// Api
import { processInitialPayment } from "../../../api/fieldRoutes";
// Store
import { useStore } from "../../../store";
// Helpers
import { extractAddressComponents } from "../../../helpers/address";
import { mapDayToNumber, mapPmTimeTo24Hour } from "../../../helpers/schedule";
// Components
import Checkbox from "../../../components/Checkbox/Checkbox";
import Button from "../../../components/Button/Button";
import FullScreenLoader from "../../../components/FullScreenLoader/FullScreenLoader";
import ContactUsToast from "../../../components/Toast/ContactUsToast/ContactUsToast";
// Styles
import styles from "./StripeCardForm.module.scss";
// JSON
import postalCodesData from "../../../assets/json/postal_codes.json";
//Amplitude
import * as amplitude from "@amplitude/analytics-browser";
import { calculateTax } from "../../../helpers/taxes";
import Input from "../../../components/Input/Input";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";

const StripeCardForm = () => {
  const {
    chosenPackage,
    addressObject,
    contactInfo,
    fieldRoutesCustomer,
    scheduleData,
    stripeData,
    setCardDetails,
  } = useStore();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [billingAddress, setBillingAddress] = useState();

  const validationSchema = yup.object({
    address: yup.string().required("Address is required"),
    unitNumber: yup.string().notRequired(),
  });

  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: contactInfo,
    mode: "onBlur",
  });

  const discountPrice = parseFloat(chosenPackage?.minimumInitialCharge) ?? 0;
  const regularPriceTax = calculateTax(
    discountPrice,
    fieldRoutesCustomer?.taxRate
  );

  const handleOnBack = () => {
    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Checkout",
        button_name: "Back",
      },
    };

    amplitude.track(buttonClickedEvent);

    navigate("/schedule");
  };

  const handleSubmit = async (e) => {
    const generalErrorEvent = {
      event_type: "Error",
      display_name: "Error",
      event_properties: {
        page_name: "eComm - Checkout",
        error_name: "Something went wrong",
      },
    };

    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Checkout",
        button_name: "Next",
      },
    };

    amplitude.track(buttonClickedEvent);

    e.preventDefault();
    setIsSubmitting(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        expand: ["customer", "payment_method"],
        payment_method_data: {
          billing_details: {
            address: extractAddressComponents(billingAddress),
          },
        },
      },
    });

    if (error) {
      console.log(error);

      const errorEvent = {
        event_type: "Error",
        display_name: "Error",
        event_properties: {
          page_name: "eComm - Checkout",
          error_name: error.decline_code,
          error_source: "Credit Card",
        },
      };

      amplitude.track(errorEvent);

      setIsSubmitting(false);

      if (error.type === "validation_error") {
        return;
      }

      if (error.type === "api_connection_error") {
        const errorEvent = {
          event_type: "Error",
          display_name: "Error",
          event_properties: {
            page_name: "eComm - Checkout",
            error_name: "Network connection lost",
          },
        };

        amplitude.track(errorEvent);

        toast(
          "Something is wrong with your network connection. Please make sure you are connected to the internet and then reload your browser",
          {
            theme: "dark",
          }
        );
        return;
      }

      amplitude.track(generalErrorEvent);

      toast(<ContactUsToast />, { theme: "dark" });
    } else {
      if (paymentIntent.status !== "succeeded") {
        setIsSubmitting(false);

        amplitude.track(generalErrorEvent);
        toast(<ContactUsToast />, { theme: "dark" });
        return;
      }

      const formattedAddress = extractAddressComponents(addressObject);
      const officeId = postalCodesData.find(
        (item) => `${item["Zip"]}` === formattedAddress.postal_code
      )?.["Office ID"];

      const formattedBillingAddress = extractAddressComponents(billingAddress);
      const billingAddressWithUnitNumber =
        contactInfo.unitNumber === ""
          ? formattedBillingAddress.line1
          : `${formattedBillingAddress.line1} ${getValues("unitNumber")}`;

      await processInitialPayment({
        serviceID: chosenPackage.typeID,
        customerID: fieldRoutesCustomer.customerID,
        preferredDays: mapDayToNumber(scheduleData?.chosenDay) ?? null,
        preferredStart:
          mapPmTimeTo24Hour(scheduleData?.chosenTime)?.start ?? null,
        preferredEnd: mapPmTimeTo24Hour(scheduleData?.chosenTime)?.end ?? null,
        officeID: isDevelopment ? 19 : officeId,
        frequency: "30",
        stripeSubscriptionId: stripeData.subscriptionId,
        billingAddress: billingAddressWithUnitNumber,
        billingCity: formattedBillingAddress.city,
        billingState: formattedBillingAddress.state,
        billingZip: formattedBillingAddress.postal_code,
      });

      setCardDetails(paymentIntent.payment_method.card);
      setIsSubmitting(false);

      const revenueEvent = {
        event_type: "Revenue",
        display_name: "Revenue",
        event_properties: {
          revenue: discountPrice + regularPriceTax,
          productId: chosenPackage.typeID,
        },
      };
      amplitude.track(revenueEvent);

      await navigate("/payment-success");
    }
  };

  const handleAgreeToTerms = () => {
    const checkboxClickedEvent = {
      event_type: "Checkbox Clicked",
      display_name: "Checkbox Clicked",
      event_properties: {
        page_name: "eComm - Checkout",
        checkbox_name: "I agree to T&C",
        checked: agreeToTerms,
      },
    };

    amplitude.track(checkboxClickedEvent);

    setAgreeToTerms((prev) => !prev);
  };

  const autoCompleteRef = useRef();
  const addressInputRef = useRef();
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);

  useEffect(() => {
    if (contactInfo) {
      addressInputRef.current.value = contactInfo.address;
      setValue("address", contactInfo.address);
    }
    if (addressObject) {
      setBillingAddress(addressObject);
    }
    //initial error set to "" and not undefined
    setError("address", { type: "custom", message: "" });
  }, [contactInfo, addressObject, setValue, setError]);

  useEffect(() => {
    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      {
        componentRestrictions: { country: "us" },
        fields: [
          "place_id",
          "address_components",
          "formatted_address",
          "name",
          "geometry",
        ],
        types: ["address"],
        language: "en",
      }
    );

    autoCompleteRef.current.addListener("place_changed", async function () {
      const place = await autoCompleteRef.current.getPlace();
      if (place) {
        addressInputRef.current.value = place.formatted_address;
        setBillingAddress(place);
        setValue("address", place.formatted_address);
        clearErrors("address");
        setError("address", {
          type: "custom",
          message: "",
        });
      }
    });
  }, [setValue, clearErrors, setBillingAddress, setError]);

  // use to apply styles to input
  useEffect(() => {
    let observer;

    // register observer with timeout to let google load autocomplete to the DOM
    setTimeout(() => {
      let elements = document.querySelectorAll(".pac-container");

      if (elements.length) {
        elements.forEach(function (element) {
          observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "style"
              ) {
                if (mutation.oldValue.includes("display: none")) {
                  setIsAddressDropdownOpen(true);
                } else {
                  setIsAddressDropdownOpen(false);
                }
              }
            });
          });

          observer.observe(element, {
            attributes: true,
            attributeFilter: ["style"],
            attributeOldValue: true,
          });
        });
      }
    }, 2000);

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const handleOnAddressBlur = () => {
    if (
      (billingAddress &&
        billingAddress.formatted_address &&
        addressInputRef.current.value !== billingAddress.formatted_address) ||
      (!billingAddress && !addressInputRef.current.value)
    ) {
      setBillingAddress(null);
      setError("address", { type: "custom", message: "Address is required" });
    }

    if (!billingAddress && addressInputRef.current.value) {
      setError("address", {
        type: "custom",
        message: "Select a verified address from the list",
      });
    }
  };

  return (
    <div>
      {isSubmitting && <FullScreenLoader />}

      <form id="payment-form" className={styles.root} onSubmit={handleSubmit}>
        <div>
          <h2 className={styles.title}>PAYMENT METHOD</h2>
          <div className={styles.divider} />
        </div>

        <div>
          <div className={styles.addressInputContainer}>
            <Input
              className={cn(styles.formInput, {
                [styles.addressInput]: isAddressDropdownOpen,
              })}
              label="Billing Address"
              placeholder="e.g. 200 Gerard St, Austin, TX 78701"
              required={true}
              autoComplete="off"
              ref={addressInputRef}
              error={errors.address?.message}
              onBlur={handleOnAddressBlur}
            />
            <Input
              className={styles.unitNumberInput}
              label="Unit/Apt #"
              placeholder="1A"
              {...register("unitNumber")}
            />
          </div>
          <PaymentElement
            options={{
              defaultValues: {
                billingDetails: {
                  name: `${contactInfo.firstName} ${contactInfo.lastName}`,
                  email: contactInfo.email,
                  phone: contactInfo.mobilePhone,
                  address: extractAddressComponents(addressObject),
                },
              },
              business: {
                name: "Magna",
              },
              fields: {
                billingDetails: {
                  address: "never",
                },
              },
            }}
          />
        </div>

        <div className={styles.tocSection}>
          <Checkbox
            label={
              <>
                I have read and agree to the{" "}
                <a
                  className={styles.tocLink}
                  href="https://www.magnapest.com/terms-conditions"
                  target="_blank"
                  rel="noreferrer"
                >
                  Magna service agreement terms and conditions
                </a>
              </>
            }
            className={styles.tocCheckbox}
            checked={agreeToTerms}
            onChange={handleAgreeToTerms}
          />
        </div>

        <div className={styles.buttonSection}>
          <Button
            block
            htmlType="submit"
            disabled={!(errors.address?.message === "" && agreeToTerms)}
          >
            Agree and Submit Payment
          </Button>
          <Button block htmlType="button" type="outline" onClick={handleOnBack}>
            Back
          </Button>
        </div>
      </form>

      <div className={styles.guaranteeSection}>
        <p>Backed by our Magna Guarantee</p>
      </div>
    </div>
  );
};

export default StripeCardForm;
